import type { SocialProviderConfig } from '@repo/api-portal-sdk/gen'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@/lib/i18n'
import {
	fetchFreshPortalSession,
	portalSessionQueryOptions
} from '@/lib/portal-session'
import { isPortalSessionAuthorized } from '@/lib/portal-session-state'
import { LoginForm } from './auth/login-form'
import { MfaChallenge } from './auth/mfa-challenge'
import { SignupWorkflow } from './auth/signup-workflow'
import { SocialSignIn } from './auth/social-sign-in'
import { AuthFrame } from './auth-frame'

export function AuthPage(props: {
	mode: 'login' | 'register'
	providers: SocialProviderConfig[]
	redirect?: string
	socialError: boolean
}) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const sessionQuery = useQuery(portalSessionQueryOptions())

	async function finishAuthentication() {
		const session = await fetchFreshPortalSession(queryClient)
		if (isPortalSessionAuthorized(session)) {
			await navigate({ to: props.redirect ?? '/dashboard' })
		}
	}

	if (sessionQuery.data?.sessionType === 'mfa_pending') {
		return (
			<AuthFrame redirect={props.redirect}>
				<MfaChallenge
					mfa={sessionQuery.data.mfa}
					onSuccess={finishAuthentication}
				/>
			</AuthFrame>
		)
	}

	return (
		<AuthFrame mode={props.mode} redirect={props.redirect}>
			{props.socialError ? (
				<AuthErrorAlert
					onDismiss={() =>
						navigate({
							replace: true,
							search: { redirect: props.redirect },
							to: props.mode === 'login' ? '/login' : '/register'
						})
					}
					title={t('common:auth.login_failed')}
				/>
			) : null}
			{props.mode === 'login' ? (
				<LoginForm onSuccess={finishAuthentication} />
			) : (
				<SignupWorkflow onSuccess={finishAuthentication} />
			)}
			{props.providers.length > 0 ? (
				<SocialSignIn
					mode={props.mode}
					providers={props.providers}
					redirect={props.redirect}
					redirectTo={props.redirect ?? '/dashboard'}
				/>
			) : null}
		</AuthFrame>
	)
}
